import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { Category, SubCategory } from '../api/models';

export const asServicePriceKey = (category: Category | string): string => {
  switch (category) {
    case Category.SEAT:
      return 'seats';
    case Category.MEAL:
      return 'meals';
    case Category.WIFI:
      return 'wifi';
    case Category.LOUNGE:
      return 'lounge';
    case Category.BAGGAGE:
      return 'baggage';
    case Category.COVER:
      return 'cover';
    default:
      return '';
  }
};

export const asCartId = (category: Category | string): string => {
  switch (category) {
    case Category.MEAL:
      return 'meals';
    case Category.SEAT:
      return 'seats';
    default:
      return category;
  }
};

export enum AdditionalCategories {
  TICKETS = 'tickets',
  TIER = 'tier',
  RESERVATION = 'reservation',
}

const categoryIcons: Record<AdditionalCategories[number] | Category[number], SvgLibraryIcon> = {
  [Category.SEAT]: SvgLibraryIcon.SEAT_OR_ECONOMY,
  [Category.BAGGAGE]: SvgLibraryIcon.CHECKED_IN_BAGGAGE,
  [Category.CABIN_BAGGAGE]: SvgLibraryIcon.CARRY_ON_BAG,
  [Category.MEAL]: SvgLibraryIcon.FOOD,
  [Category.WIFI]: SvgLibraryIcon.WIFI,
  [Category.LOUNGE]: SvgLibraryIcon.LOUNGE,
  [Category.COVER]: SvgLibraryIcon.INSURANCE,
  [Category.SAF]: SvgLibraryIcon.SAF,
  [Category.PET]: SvgLibraryIcon.PET,
  [Category.SPORT]: SvgLibraryIcon.BIKE,
  [Category.SPECIAL]: SvgLibraryIcon.EXTRA_CHECK_IN_BAGGAGE,
  [Category.FIREARM]: SvgLibraryIcon.FIRE_ARMS,
  [Category.MEDIC]: SvgLibraryIcon.MEDICAL_ASSISTANCE,
  [Category.SPECIAL_NEED]: SvgLibraryIcon.PERSON_SPECIAL_ASSIST,
  [Category.CHILD]: SvgLibraryIcon.CHILD,
  [Category.OTHER]: SvgLibraryIcon.INFO,
  [Category.SEAT_WIFI]: SvgLibraryIcon.WIFI,
  [Category.SEAT_CABIN_BAGGAGE]: SvgLibraryIcon.CARRY_ON_BAG_TROLLEY,
  [Category.SEAT_MEAL]: SvgLibraryIcon.FOOD,
  [Category.SEAT_PRIORITY]: SvgLibraryIcon.PRIORITY_SECURITY,
  [Category.LOUNGE_CABIN_BAGGAGE]: SvgLibraryIcon.CARRY_ON_BAG_TROLLEY,
  [Category.LOUNGE_WIFI]: SvgLibraryIcon.WIFI,
  [Category.LOUNGE_PRIORITY]: SvgLibraryIcon.PRIORITY_SECURITY,
  [Category.WIFI_MEAL]: SvgLibraryIcon.FOOD,
  [Category.PRIORITY]: SvgLibraryIcon.PRIORITY_SECURITY,
  [AdditionalCategories.TIER]: SvgLibraryIcon.FINNAIR_PLUS,
  [AdditionalCategories.TICKETS]: SvgLibraryIcon.FLIGHT_STATUS,
  [AdditionalCategories.RESERVATION]: SvgLibraryIcon.RESERVATION,
};

export const getIconForServiceCategory = (category: Category | string): SvgLibraryIcon =>
  (categoryIcons[category] ?? categoryIcons.other) as SvgLibraryIcon;

const subCategoryIcons: Record<SubCategory, SvgLibraryIcon> = {
  [SubCategory.BAGGAGE]: SvgLibraryIcon.CHECKED_IN_BAGGAGE,
  [SubCategory.OVERSIZED_BAGGAGE]: SvgLibraryIcon.EXTRA_CHECK_IN_BAGGAGE,
  [SubCategory.CHECKED_BAGGAGE]: SvgLibraryIcon.CHECKED_BAGGAGE,
  [SubCategory.CHECKED_BAGGAGE_NON_AY]: SvgLibraryIcon.CHECKED_BAGGAGE,
  [SubCategory.CABIN_BAGGAGE]: SvgLibraryIcon.CARRY_ON_BAG,
  [SubCategory.CABIN_BAGGAGE_NON_AY]: SvgLibraryIcon.CARRY_ON_BAG,
  [SubCategory.CABIN_ITEM]: SvgLibraryIcon.BACKPACK,
  [SubCategory.CABIN_INFANT]: SvgLibraryIcon.INFANT,
  [SubCategory.CABIN_WEIGHT]: SvgLibraryIcon.BAGGAGE_WEIGHT,
  [SubCategory.CARRY_ON_AT_CHECK_IN]: SvgLibraryIcon.CARRY_ON_BAG,
  [SubCategory.MEAL]: SvgLibraryIcon.FOOD,
  [SubCategory.SPECIAL_DIET]: SvgLibraryIcon.MEAL,
  [SubCategory.SEAT]: SvgLibraryIcon.SEAT_OR_ECONOMY,
  [SubCategory.WIFI]: SvgLibraryIcon.WIFI,
  [SubCategory.LOUNGE]: SvgLibraryIcon.LOUNGE,
  [SubCategory.MEDICAL]: SvgLibraryIcon.MEDICAL_ASSISTANCE,
  [SubCategory.ASSISTANCE]: SvgLibraryIcon.PERSON_SPECIAL_ASSIST,
  [SubCategory.PETS]: SvgLibraryIcon.PET_IN_BAG,
  [SubCategory.TRAVEL_COMFORT]: SvgLibraryIcon.REST,
  [SubCategory.COVER]: SvgLibraryIcon.INSURANCE,
  [SubCategory.OTHER]: SvgLibraryIcon.INFO,
  [SubCategory.PET_IN_HOLD]: SvgLibraryIcon.DOG,
  [SubCategory.PET_IN_CABIN]: SvgLibraryIcon.PET_IN_BAG,
  [SubCategory.BIKE]: SvgLibraryIcon.BIKE,
  [SubCategory.WINDSURFING]: SvgLibraryIcon.SURFBOARD,
  [SubCategory.SKIS]: SvgLibraryIcon.SKIS,
  [SubCategory.SKI_JUMPING]: SvgLibraryIcon.SKIS,
  [SubCategory.SPORTS_MEDIUM]: SvgLibraryIcon.SPORTS_EQUIPMENT,
  [SubCategory.SPORTS_LARGE]: SvgLibraryIcon.SPORTS_EQUIPMENT,
  [SubCategory.HOCKEY]: SvgLibraryIcon.SPORTS_EQUIPMENT,
  [SubCategory.GOLF]: SvgLibraryIcon.GOLF,
  [SubCategory.SURF]: SvgLibraryIcon.SURFBOARD,
  [SubCategory.VAULTING_POLE]: SvgLibraryIcon.SPORTS_EQUIPMENT,
  [SubCategory.TIER_BENEFIT_SPORT_EQUIPMENT]: SvgLibraryIcon.SPORTS_EQUIPMENT,
  [SubCategory.TANDEM_BIKE]: SvgLibraryIcon.BIKE,
  [SubCategory.SPECIAL_EQUIPMENT]: SvgLibraryIcon.EXTRA_CHECK_IN_BAGGAGE,
  [SubCategory.MUSICAL_MEDIUM]: SvgLibraryIcon.MUSIC,
  [SubCategory.MUSICAL_LARGE]: SvgLibraryIcon.MUSIC,
  [SubCategory.MEDIA]: SvgLibraryIcon.MUSIC,
  [SubCategory.HEAVY_OR_LARGE]: SvgLibraryIcon.BAGGAGE_WEIGHT,
  [SubCategory.FIREARM]: SvgLibraryIcon.FIRE_ARMS,
  [SubCategory.AMMUNITION]: SvgLibraryIcon.FIRE_ARMS,
  [SubCategory.OXYGEN]: SvgLibraryIcon.MEDICAL_ASSISTANCE,
  [SubCategory.ASSISTANCE_ITEMS]: SvgLibraryIcon.PERSON_WHEELCHAIR,
  [SubCategory.ASSISTANCE_SERVICES]: SvgLibraryIcon.PERSON_SPECIAL_ASSIST,
  [SubCategory.CHILD_ASSISTANCE]: SvgLibraryIcon.CHILD,
  [SubCategory.FRESH_MEAL]: SvgLibraryIcon.FOOD,
  [SubCategory.HOT_PRE_ORDER_MEAL]: SvgLibraryIcon.PRE_ORDER,
  [SubCategory.EMOTIONAL_SUPPORT_ANIMAL]: SvgLibraryIcon.DOG,
  [SubCategory.FAST_TRACK]: SvgLibraryIcon.PRIORITY_SECURITY,
  [SubCategory.PRIORITY_SERVICES]: SvgLibraryIcon.PRIORITY_SECURITY,
  [SubCategory.SAF_LOW]: SvgLibraryIcon.SAF,
  [SubCategory.SAF_MID]: SvgLibraryIcon.SAF,
  [SubCategory.SAF_HIGH]: SvgLibraryIcon.SAF,
  [SubCategory.SEAT_WIFI]: SvgLibraryIcon.WIFI_AIRCRAFT,
  [SubCategory.SEAT_HOT_PRE_ORDER_MEAL]: SvgLibraryIcon.FOOD,
  [SubCategory.SEAT_FRESH_MEAL]: SvgLibraryIcon.FOOD,
  [SubCategory.SEAT_CABIN_BAGGAGE]: SvgLibraryIcon.CARRY_ON_BAG,
  [SubCategory.SEAT_PRIORITY_SERVICES]: SvgLibraryIcon.PRIORITY_CHECK_IN,
  [SubCategory.WIFI_FRESH_MEAL]: SvgLibraryIcon.FOOD,
  [SubCategory.WIFI_HOT_PRE_ORDER_MEAL]: SvgLibraryIcon.FOOD,
  [SubCategory.LOUNGE_CABIN_BAGGAGE]: SvgLibraryIcon.CARRY_ON_BAG,
  [SubCategory.LOUNGE_PRIORITY_SERVICES]: SvgLibraryIcon.PRIORITY_SECURITY,
  [SubCategory.LOUNGE_WIFI]: SvgLibraryIcon.WIFI,
};

export const getIconForSubCategory = (subCategory: SubCategory | string): SvgLibraryIcon | undefined =>
  subCategoryIcons[subCategory as SubCategory];
